import { useEffect, useRef, useState } from 'react';
import { postJson } from '../lib/api.ts';
import { getConsumer } from '../lib/cable.ts';

export type EmbeddedProvider = 'd4sign' | 'zapsign';

type StartResponse = { embed_url?: string; signed?: boolean };

// Mensagens que cada iframe manda por postMessage: quando o paciente assinou e quando
// o PDF assinado já pode ser baixado (na D4Sign as duas chegam juntas).
const PROVIDERS: Record<EmbeddedProvider, { signedEvent: string; readyEvent: string; hint: string; safariFix: boolean }> = {
  d4sign: {
    signedEvent: 'signed',
    readyEvent: 'signed',
    hint: 'Confira o termo e toque em assinar. Você receberá um código no seu WhatsApp para confirmar.',
    safariFix: true,
  },
  zapsign: {
    signedEvent: 'zs-doc-signed',
    readyEvent: 'zs-signed-file-ready',
    hint: 'Confira o termo, aceite e desenhe sua assinatura.',
    safariFix: false,
  },
};

interface EmbeddedSignatureProps {
  provider: EmbeddedProvider;
  code: string;
  signed: boolean;
  onSigned: () => void;
  onFallback?: (() => void) | undefined;
}

// O script oficial da D4Sign faz o Safari passar uma vez pelo domínio deles para
// liberar o cookie do iframe. O retorno vai codificado para não perder o hash da rota.
const redirectForSafariCookie = (embedUrl: string) => {
  const userAgent = navigator.userAgent;
  const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome');
  if (!isSafari || /^(.*;)?\s*fixed\s*=\s*[^;]+(.*)?$/.test(document.cookie)) return false;

  document.cookie = 'fixed=fixed; expires=Tue, 19 Jan 2038 03:14:07 UTC; path=/';
  const origin = new URL(embedUrl).origin;
  window.location.replace(`${origin}/embed/safari_fix?param=&r=${encodeURIComponent(window.location.href)}`);
  return true;
};

const EmbeddedSignature = ({ provider, code, signed, onSigned, onFallback }: EmbeddedSignatureProps) => {
  const config = PROVIDERS[provider];
  const [embedUrl, setEmbedUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [wrongData, setWrongData] = useState(false);
  const [error, setError] = useState('');
  const startedRef = useRef(false);
  const onSignedRef = useRef(onSigned);
  onSignedRef.current = onSigned;

  const start = async () => {
    setIsLoading(true);
    setError('');

    try {
      const result = await postJson<StartResponse>(`/api/agreement/${code}/${provider}`, {});

      if (result.signed) {
        onSignedRef.current();
      } else if (result.embed_url && !(config.safariFix && redirectForSafariCookie(result.embed_url))) {
        setEmbedUrl(result.embed_url);
      }
    } catch (error) {
      setError((error as Error).message || 'Erro ao preparar a assinatura.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Cada preparação gasta chamadas da API do provedor: evita a segunda montagem do StrictMode.
    if (startedRef.current) return;
    startedRef.current = true;
    start();
  }, [code]);

  useEffect(() => {
    if (!embedUrl) return;
    const origin = new URL(embedUrl).origin;

    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== origin) return;
      if (event.data === 'wrong-data') setWrongData(true);
      if (event.data === config.signedEvent) setIsConfirming(true);
      if (event.data !== config.readyEvent) return;

      setIsConfirming(true);
      try {
        const result = await postJson<{ verified: boolean }>(`/api/agreement/${code}/${provider}/confirm`, {});
        if (result.verified) onSignedRef.current();
      } catch (error) {
        console.error(error);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [embedUrl, code, provider]);

  // Se o provedor ainda estiver finalizando o documento na confirmação, o webhook
  // grava o termo assinado e avisa por aqui.
  useEffect(() => {
    const subscription = getConsumer().subscriptions.create(
      { channel: 'PaymentChannel', agreement_code: code },
      {
        received: (data: { status?: string }) => {
          if (data.status === 'signed') onSignedRef.current();
        },
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [code]);

  return (
    <section className="space-y-4 rounded-3xl bg-white p-4 shadow-sm sm:p-8">
      {isLoading && <p className="text-sm text-slate-500">Preparando o termo para assinatura...</p>}

      {embedUrl && (
        <>
          <p className="text-sm text-slate-500">{config.hint}</p>
          <iframe
            src={embedUrl}
            title="Assinatura do termo"
            allow="geolocation; camera"
            className="h-[80vh] min-h-[560px] w-full rounded-3xl border border-slate-200 bg-white"
          />
        </>
      )}

      {isConfirming && !signed && (
        <p className="text-sm text-slate-500">Assinatura recebida! Finalizando o documento, isso leva alguns segundos...</p>
      )}

      {wrongData && (
        <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">
          Se algum dado seu está incorreto, fale com a clínica para corrigir antes de assinar.
        </div>
      )}

      {error && <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {(error || wrongData) && onFallback && (
        <button
          type="button"
          onClick={onFallback}
          className="w-full rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-red-50"
        >
          Assinar de outra forma
        </button>
      )}
    </section>
  );
};

export default EmbeddedSignature;
